/**
 * firstEmailToast.ts — #1892.
 *
 * Kasandra got "Your first forwarded email landed" at 8:32 PM on Oct 22, one
 * minute after `inbox_setup_completed_at` was written, having forwarded emails
 * since August 4.
 *
 * Two causes, and this module answers the first half of each:
 *
 * 1. `markFirstInboxSuccessIfNeeded` named a column `inbox_addresses` does not
 *    have in its RETURNING, so the flag write failed silently from 2026-07-19
 *    until the #1568 sweep fixed it around 2026-09-12. Everyone who forwarded
 *    in that window stayed unflagged, so their NEXT successful email is treated
 *    as their first. The re-backfill migration repairs the data; this makes the
 *    toast harmless if any case was missed.
 *
 * 2. The "seen" flag is only localStorage. A new device, a browser instead of
 *    the Median app, a reinstall, or iOS clearing web data all show it again,
 *    however long ago the first email actually was.
 *
 * ⚠️ A 72-hour window fixes BOTH, because they share one shape: a celebration
 * for a thing that happened months ago is not a celebration, it is a bug she
 * can see. Something that happened in the last three days is worth saying
 * once; anything older is not worth saying at all.
 */

/** How recent "first email" has to be for the toast to still mean something. */
export const FIRST_EMAIL_TOAST_WINDOW_HOURS = 72;

/**
 * Should the toast be shown?
 *
 * False for null (never completed), for an unparseable value, and for anything
 * older than 72 hours.
 *
 * ⚠️ A FUTURE timestamp returns TRUE, deliberately. Clock skew of a few
 * seconds between the database and her phone is ordinary, and it lands on the
 * one case the toast is actually for: an email processed moments ago. Refusing
 * it would silence the celebration at exactly the moment it is true.
 */
export function shouldShowFirstEmailToast(
  completedAt: string | null | undefined,
  now: Date,
): boolean {
  if (!completedAt) return false;
  const at = new Date(completedAt).getTime();
  // ⚠️ Belt and braces, and currently INERT — a mutation run confirmed that
  // removing it changes nothing, because NaN fails both comparisons below and
  // the function returns false anyway. Kept so a future edit to the arithmetic
  // cannot turn an unparseable timestamp into a celebration by accident.
  if (Number.isNaN(at)) return false;
  const nowMs = now.getTime();
  if (Number.isNaN(nowMs)) return false;
  const ageMs = nowMs - at;
  if (ageMs < 0) return true;
  return ageMs <= FIRST_EMAIL_TOAST_WINDOW_HOURS * 3_600_000;
}
